import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Table, Form, Button, Breadcrumb, Row, Col, InputGroup } from "react-bootstrap";
import { FaPlus, FaRegWindowClose, FaBarcode } from "react-icons/fa";
import PieChart from "../components/PieChart";
import Message from "../components/Message";
import Loader from '../components/Loader';
import { useGetProductDetailsQuery } from '../slices/productsApiSlice';

const VerkoopScreen = () => {
  const { isLoading, error } = useGetProductDetailsQuery();

  const navigate = useNavigate();

  const [isbn, setIsbn] = useState("");
  const [message, setMessage] = useState({ variant: "", text: "" });
  const [books, setBooks] = useState(() => {
    const savedBooks = localStorage.getItem("books");
    return savedBooks ? JSON.parse(savedBooks) : [];
  });
  const [totalPrice, setTotalPrice] = useState(() => {
    return parseFloat(localStorage.getItem("totalPrice")) || 0;
  });
  const [totalWeight, setTotalWeight] = useState(() => {
    return parseFloat(localStorage.getItem("totalWeight")) || 0;
  });
  
  useEffect(() => {
    localStorage.setItem("books", JSON.stringify(books));
    localStorage.setItem("totalPrice", totalPrice);
    localStorage.setItem("totalWeight", totalWeight);
  }, [books, totalPrice, totalWeight]);

  const fetchBookData = async () => {
    try {
      const response = await axios.get(
        `https://www.googleapis.com/books/v1/volumes?q=isbn:${isbn}`
      );

      if (response.data.items && response.data.items.length > 0) {
        const book = response.data.items[0].volumeInfo;
        const randomPrice = (Math.random() * 2 + 1).toFixed(2);
        const weight = book.dimensions?.weight || 0.3;

        const newBook = {
          isbn: book.industryIdentifiers?.find((id) => id.type === "ISBN_13")?.identifier || isbn,
          title: book.title || "Onbekende titel",
          author: book.authors ? book.authors.join(", ") : "Onbekende auteur",
          price: parseFloat(randomPrice),
          weight,
        };

        setBooks((prevBooks) => [...prevBooks, newBook]);
        setTotalPrice((prevPrice) => prevPrice + parseFloat(randomPrice));
        setTotalWeight((prevWeight) => prevWeight + weight);
        setIsbn("");
        setMessage({ variant: "success", text: "Boek succesvol toegevoegd!" });

        setTimeout(() => setMessage({ variant: "", text: "" }), 3000);
      } else {
        setMessage({ variant: "danger", text: "Geen boek gevonden voor dit ISBN." });
        setTimeout(() => setMessage({ variant: "", text: "" }), 3000);
      }
    } catch (error) {
      console.error("Fout bij ophalen boekgegevens:", error);
      setMessage({ variant: "danger", text: "Er is een fout opgetreden bij het ophalen van de boekgegevens." });
      setTimeout(() => setMessage({ variant: "", text: "" }), 3000);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isbn.trim()) {
      fetchBookData();
    } else {
      setMessage({ variant: "danger", text: "Voer een geldig ISBN in." });
      setTimeout(() => setMessage({ variant: "", text: "" }), 3000);
    }
  };

  const handleRemoveBook = (index) => {
    const removedBook = books[index];
    setBooks((prevBooks) => prevBooks.filter((_, i) => i !== index));
    setTotalPrice((prevPrice) => prevPrice - removedBook.price);
    setTotalWeight((prevWeight) => prevWeight - removedBook.weight);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const remainingWeight = Math.max(23 - totalWeight, 0).toFixed(2);
  const remainingPrice = Math.max(20 - totalPrice, 0).toFixed(2);
  const isEligibleForCart = totalWeight >= 2 || totalPrice >= 2;

  const handleAddToCart = () => {
    if (isEligibleForCart) {
      navigate("/cart"); // Redirect to cart page
    } else {
      setMessage({
        variant: "info",
        text: `Je hebt nog ${remainingWeight} kg of €${remainingPrice} nodig voor een gratis verzendlabel.`,
      });
      setTimeout(() => setMessage({ variant: "", text: "" }), 5000);
    }
  };

  const startBarcodeScanner = () => {
    setMessage({ variant: "info", text: "Barcodescanner starten..." });
    setTimeout(() => setMessage({ variant: "", text: "" }), 3000);
  };

  const totalBoxes = Math.ceil(totalWeight / 23);
  const weightPercentage = Math.min((totalWeight / 23) * 100, 100).toFixed(1);

  return (
    <>
    { isLoading ? (
     <Loader />
     ) : error ? (
       <Message variant='danger'>{error?.data?.message || error.error}</Message>
     ) : (
    <div className="py-3">
      {message.text && <Message variant={message.variant}>{message.text}</Message>}
      {/* Breadcrumb */}
      <Breadcrumb>
        <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
        <Breadcrumb.Item active>Boeken verkopen</Breadcrumb.Item>
      </Breadcrumb>

      <h3 className="d-flex justify-content-center align-items-center">Boeken verkopen</h3>

      <Row xs={9} md={6} className="mt-3 mb-4 d-flex justify-content-center align-items-center">
        <Col>
          <div className="d-flex justify-content-center align-items-center">
            <strong>Aantal boeken:</strong>
          </div>
          <div className="d-flex justify-content-center align-items-center">
            {books.length}
          </div>
        </Col>
        <Col>
          <div className="d-flex justify-content-center align-items-center">
            <strong>Totale prijs:</strong>{" "}
          </div>
          <div className="d-flex justify-content-center align-items-center">
            <span style={{ color: "#d9534f", fontWeight: "bold" }}>
              €{totalPrice.toFixed(2)}
            </span>
          </div>
        </Col>
        <Col>
          <div className="d-flex justify-content-center align-items-center">
            <strong>Number of Boxes:</strong>
          </div>
          <div className="d-flex justify-content-center align-items-center">
            {totalBoxes}
          </div>
        </Col>
        { weightPercentage > 0 &&
        <Col>
          <div className="d-flex justify-content-center align-items-center">
            <PieChart progress={weightPercentage} />
          </div>
          <div className="d-flex justify-content-center align-items-center">
            {totalWeight.toFixed(2)}/23kg
          </div>
        </Col>}
      </Row>

      {/* Textfield and Button Row */}
      <Row className="mb-3 d-flex justify-content-center align-items-center">
        <Col xs={9} md={6} className="d-flex justify-content-center align-items-center">
          <InputGroup>
            <Form.Control
              type="text"
              placeholder="Enter ISBN number"
              value={isbn}
              onChange={(e) => setIsbn(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <Button
              variant="outline-secondary"
              onClick={startBarcodeScanner}
              style={{ borderLeft: "none" }}
            >
              <FaBarcode />
            </Button>
            <Button
              type="submit"
              variant="success"
              className="ms-2"
              onClick={handleSubmit}
              style={{ color: "#fff" }}
            >
              <FaPlus />
            </Button>
          </InputGroup>
        </Col>
      </Row>

      {books.length > 0 && (
        <Table xs={9} md={6} striped bordered hover responsive>
          <thead>
            <tr>
              <th>Title & Author</th>
              <th>ISBN</th>
              <th>Price / Weight</th>
            </tr>
          </thead>
          <tbody>
            {books.map((book, index) => (
              <tr key={index}>
                <td>
                  {book.title} <br /> <small>by {book.author}</small>
                </td>
                <td>
                  {book.isbn}
                </td>
                <td className="position-relative">
                  €{book.price.toFixed(2)} / {book.weight.toFixed(2)} kg
                  <Button
                    variant="danger"
                    size="sm"
                    className="position-absolute top-0 end-0"
                    onClick={() => handleRemoveBook(index)}
                    style={{
                      fontSize: "1.5em",
                      padding: "0.01em",
                      backgroundColor: "transparent",
                      borderColor: "transparent",
                    }}
                  >
                    <FaRegWindowClose />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
    )}
    {/* {books.length > 0 && (
    <div className="d-flex justify-content-center mt-3">
      <Button variant="primary" size="md" disabled={books.length === 0}>
        Add to Cart
      </Button>
    </div>
    )
    } */}
        {!isEligibleForCart && (
        <div className="d-flex justify-content-center mt-3">
          <p className="text-danger">
            You need {remainingWeight} kg or €{remainingPrice} more to get a free shipping label.
          </p>
        </div>
        )}
      <div className="d-flex justify-content-center mt-3">
        <Button variant="primary" onClick={handleAddToCart} disabled={!isEligibleForCart}>
          Add to Cart
        </Button>
      </div>
    </>
  );
};

export default VerkoopScreen;
